/**
 * 剧集收藏 API 服务
 * 提供与后端 MongoDB favorite API 的交互接口
 */

import axios from 'axios';

const API_BASE = '/api/v2/favorite';

const favoriteApi = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器 - 添加认证信息
favoriteApi.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/**
 * 格式化日期时间
 * @param {string|Date} date - 日期
 * @returns {string} 格式化后的日期时间
 */
export const formatFavoriteDate = (date) => {
  if (!date) return '';

  const d = new Date(date);
  const now = new Date();
  const diff = now - d;
  const minutes = Math.floor(diff / (1000 * 60));
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (minutes < 1) return '刚刚';
  if (minutes < 60) return `${minutes}分钟前`;
  if (hours < 24) return `${hours}小时前`;
  if (days === 1) return '昨天';
  if (days < 7) return `${days}天前`;

  return d.toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

/**
 * 添加收藏
 * @param {Object} data - 收藏数据
 * @param {string} data.user_id - 用户ID
 * @param {string} data.drama_id - 剧集ID
 * @returns {Promise} API 响应
 */
export const addFavorite = async (data) => {
  try {
    const response = await favoriteApi.post('/add', data);
    return response.data;
  } catch (error) {
    console.error('添加收藏失败:', error);
    return {
      success: false,
      message: error.response?.data?.message || '添加失败',
    };
  }
};

/**
 * 取消收藏
 * @param {Object} data - 收藏数据
 * @param {string} data.user_id - 用户ID
 * @param {string} data.drama_id - 剧集ID
 * @returns {Promise} API 响应
 */
export const removeFavorite = async (data) => {
  try {
    const response = await favoriteApi.post('/remove', data);
    return response.data;
  } catch (error) {
    console.error('取消收藏失败:', error);
    return {
      success: false,
      message: error.response?.data?.message || '取消失败',
    };
  }
};

/**
 * 检查是否已收藏
 * @param {string} userId - 用户ID
 * @param {string} dramaId - 剧集ID
 * @returns {Promise} API 响应
 */
export const checkFavorite = async (userId, dramaId) => {
  try {
    const response = await favoriteApi.get('/check', {
      params: { user_id: userId, drama_id: dramaId },
    });
    return response.data;
  } catch (error) {
    console.error('检查收藏状态失败:', error);
    return {
      success: false,
      message: error.response?.data?.message || '检查失败',
    };
  }
};

/**
 * 获取用户收藏列表（带剧集详情，分页）
 * @param {string} userId - 用户ID
 * @param {number} page - 页码
 * @param {number} pageSize - 每页数量
 * @returns {Promise} API 响应
 */
export const getFavorites = async (userId, page = 1, pageSize = 20) => {
  try {
    const response = await favoriteApi.get(`/list/${userId}`, {
      params: { page, page_size: pageSize },
    });
    return response.data;
  } catch (error) {
    console.error('获取收藏列表失败:', error);
    return {
      success: false,
      message: error.response?.data?.message || '获取失败',
    };
  }
};

/**
 * 获取用户收藏数量
 * @param {string} userId - 用户ID
 * @returns {Promise} API 响应
 */
export const getFavoriteCount = async (userId) => {
  try {
    const response = await favoriteApi.get(`/count/${userId}`);
    return response.data;
  } catch (error) {
    console.error('获取收藏数量失败:', error);
    return {
      success: false,
      message: error.response?.data?.message || '获取失败',
    };
  }
};

/**
 * 获取剧集被收藏数量
 * @param {string} dramaId - 剧集ID
 * @returns {Promise} API 响应
 */
export const getDramaFavoriteCount = async (dramaId) => {
  try {
    const response = await favoriteApi.get(`/drama-count/${dramaId}`);
    return response.data;
  } catch (error) {
    console.error('获取剧集收藏数量失败:', error);
    return {
      success: false,
      message: error.response?.data?.message || '获取失败',
    };
  }
};

/**
 * 获取当前用户的 user_id
 * 从 localStorage 中读取 Mongo 用户信息
 * @returns {string|null} 用户ID 或 null（未登录时）
 */
export const getCurrentUserId = () => {
  try {
    const userStr = localStorage.getItem('mongo_user');
    if (userStr) {
      const user = JSON.parse(userStr);
      return user.user_id || user._id || user.id || user.userId;
    }
  } catch (e) {
    console.error('解析用户信息失败:', e);
  }
  return null;
};

/**
 * 切换收藏状态（智能添加/取消）
 * @param {string} userId - 用户ID
 * @param {string} dramaId - 剧集ID
 * @param {boolean} isCurrentlyFavorited - 当前是否已收藏
 * @returns {Promise} API 响应
 */
export const toggleFavorite = async (userId, dramaId, isCurrentlyFavorited) => {
  if (isCurrentlyFavorited) {
    return await removeFavorite({ user_id: userId, drama_id: dramaId });
  } else {
    return await addFavorite({ user_id: userId, drama_id: dramaId });
  }
};

export default favoriteApi;
